'use client';

import React, { createContext, useContext, useMemo } from 'react';

import { useGlobalConfig } from '@/components/GlobalContext';

import { App, Maturity } from '@/services/graphQL/api/generated/types_web';

const HOME_TITLES = ['home', 'accueil'];

const AppContext = createContext<{
  app: App | undefined | null;
  homeNavId: string;
  homeNavResolved: boolean;
  liveNavId: string;
  moviesNavId: string;
  showsNavId: string;
  sportsNavId: string;
  kidsNavId: string;
  proudlyCanadianNavId: string;
  navigationLinks: any[];
  corporateLinks: any[];
  loading: boolean;
  error: any;
  refetch: () => void;
}>({
  app: null,
  homeNavId: '',
  homeNavResolved: false,
  liveNavId: '',
  moviesNavId: '',
  showsNavId: '',
  sportsNavId: '',
  kidsNavId: '',
  proudlyCanadianNavId: '',
  navigationLinks: [],
  corporateLinks: [],
  loading: false,
  error: null,
  refetch: () => {},
});

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({
  children,
  app,
  loading = false,
  error = null,
  refetch = () => {},
}: {
  children: React.ReactNode;
  app: App | undefined | null;
  loading?: boolean;
  error?: any;
  refetch?: () => void;
}) => {
  const { userMaturity } = useGlobalConfig();

  const filteredNavigationData = useMemo(() => {
    if (!app?.navigation) {
      return {
        navigationLinks: [],
        corporateLinks: [],
      };
    }

    let navigationLinks = app.navigation.navigationLinks || [];
    let corporateLinks = app.navigation.corporateLinks || [];

    // Filter out nav items based on user maturity
    // If the user is not an adult, remove items with logoType 'LIVE'
    if (userMaturity !== Maturity.Adult) {
      navigationLinks = navigationLinks.filter((navLink) => {
        return navLink?.logoType !== 'LIVE';
      });
    }

    return {
      navigationLinks,
      corporateLinks,
    };
  }, [app?.navigation, userMaturity]);

  const navIds = useMemo(() => {
    let homeNavId = '';
    let liveNavId = '';
    let sportsNavId = '';
    let moviesNavId = '';
    let kidsNavId = '';
    let showsNavId = '';
    let proudlyCanadianNavId = '';

    for (let i = 0; i < filteredNavigationData.navigationLinks.length; i++) {
      const nav = filteredNavigationData.navigationLinks[i];
      if (!nav || !nav.link) continue;

      const action = nav.link.action as { id?: string } | undefined;
      if (!action) continue;

      const actionId = action.id;
      if (!actionId) continue;

      const logoType = String(nav.logoType || '').toUpperCase();
      const title = String(nav.link?.displayTitle || '').toLowerCase();

      if (!homeNavId && HOME_TITLES.includes(title)) {
        homeNavId = actionId;
      }
      if (!liveNavId && logoType === 'LIVE') {
        liveNavId = actionId;
      }
      if (!sportsNavId && logoType === 'SPORTS') {
        sportsNavId = actionId;
      }
      if (!moviesNavId && logoType === 'MOVIES') {
        moviesNavId = actionId;
      }
      if (!kidsNavId && logoType === 'KIDS') {
        kidsNavId = actionId;
      }
      if (!showsNavId && ['SHOWS', 'SERIES', 'EMISSIONS'].includes(logoType)) {
        showsNavId = actionId;
      }
      if (!proudlyCanadianNavId && logoType.startsWith('CUSTOM_CANADA')) {
        proudlyCanadianNavId = actionId;
      }
      if (
        homeNavId &&
        liveNavId &&
        sportsNavId &&
        moviesNavId &&
        showsNavId &&
        proudlyCanadianNavId &&
        kidsNavId
      ) {
        break;
      }
    }

    return {
      homeNavId,
      liveNavId,
      sportsNavId,
      moviesNavId,
      showsNavId,
      proudlyCanadianNavId,
      kidsNavId,
    };
  }, [filteredNavigationData.navigationLinks]);

  const {
    homeNavId,
    liveNavId,
    sportsNavId,
    moviesNavId,
    showsNavId,
    proudlyCanadianNavId,
    kidsNavId,
  } = navIds;

  const homeNavResolved = !loading && app !== undefined;

  return (
    <AppContext.Provider
      value={{
        app,
        homeNavId,
        homeNavResolved,
        liveNavId,
        moviesNavId,
        showsNavId,
        sportsNavId,
        kidsNavId,
        proudlyCanadianNavId,
        navigationLinks: filteredNavigationData.navigationLinks,
        corporateLinks: filteredNavigationData.corporateLinks,
        loading,
        error,
        refetch,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
